



import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useSidebar } from "../context/SidebarContext";
import {
  LayoutDashboard,
  Users,
  CreditCard,
  Wallet,
  History,
  LogOut,
  ArrowUpCircle,
  ArrowDownCircle,
  Percent,
  Bell,
  Gamepad,
  Link as LinkIcon,
  Gift,
  Settings,
  ChevronDown,
  Shield,
  File,
  MessageCircle,
  UserCheck,
  List,
  Radio,
  Building2,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import logo from "../assets/logo.png";
import ArrowAccordion from "../assets/icon-dropdown.png";
import { isOwnerRole } from "@/utils/roles";
import "./Sidebar.css";

const GROUP_ACCESS = {
  SuperAdmin: ["main", "management", "features", "system"],
  Admin: ["main", "management", "features", "system"],
  SubAdmin: ["main", "management", "features"],
  Dealer: ["main", "management", "features"],
  Agent: ["main", "management"],
  SubAgent: ["main", "management"]
};

const Sidebar = ({ isSidebarOpen, toggleSidebar }) => {
  const {
    isSidebarOpen: contextSidebarOpen,
    toggleSidebar: contextToggleSidebar,
    expandedItem,
    setExpandedItem,
    sidebarScrollPos
  } = useSidebar();
  const sidebarState = isSidebarOpen ?? contextSidebarOpen;
  const toggleFunction = toggleSidebar ?? contextToggleSidebar;
  const navigate = useNavigate();
  const location = useLocation();
  // Removed local state: const [expandedItem, setExpandedItem] = useState("/account-management");
  const sidebarRef = useRef(null);
  const userRole = localStorage.getItem("userRole") || "Guest";
  const [showToggleBar, setShowToggleBar] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const allowedGroups = GROUP_ACCESS[userRole] ?? [];

  const menuItems = [
    {
      path: "/user",
      label: "Agency Management",
      icon: <LayoutDashboard size={18} />,
      hasDropdown: true,
      children: [
        { path: "/agent-listing", label: "Agent Listing", icon: <LayoutDashboard size={18} /> },
        // { path: "/user", label: "User Listing", icon: <LayoutDashboard size={18} /> },
        { path: "/transfer", label: "Transfer", icon: <LayoutDashboard size={18} /> },
        { path: "/lords-notification", label: "Notification", icon: <LayoutDashboard size={18} /> },
      ]
    },

    {
      path: "/risk-management",
      label: "Risk Management",
      icon: <LayoutDashboard size={18} />,
      hasDropdown: true,
      children: [
        { path: "/netexpouser", label: "Net Exposure", icon: <LayoutDashboard size={18} /> },
        { path: "/bet-ticker", label: "Bet Ticker", icon: <LayoutDashboard size={18} /> },
      ]
    },

    {
      path: "/reports",
      label: "Reports",
      icon: <File size={18} />,
      hasDropdown: true,
      children: [
        { path: "/mybets", label: "My Bets Report", icon: <List size={16} /> },
        { path: "/plreport", label: "Profit Loss Report", icon: <Percent size={16} /> },
        { path: "/transfer-statement", label: "Transfer Statement", icon: <ArrowUpCircle size={16} /> },
        { path: "/admin/reports/casinoresult", label: "Casino Report", icon: <Gamepad size={16} /> },
        { path: "/game", label: "Game Report", icon: <Gamepad size={16} /> },
        { path: "/message-report", label: "Message Report", icon: <MessageCircle size={16} /> },

      ]
    },

    {
      path: "/account-management",
      label: "Account Management",
      icon: <Settings size={18} />,
      hasDropdown: true,
      children: [

        { path: "/account-statement", label: "Account Statement", icon: <File size={16} /> },
        { path: "/clients-account-statement", label: "Clients Account Statement", icon: <Users size={16} /> },
        { path: "/dashboardhome", label: "Balance", icon: <Wallet size={16} /> },


      ]
    },
    {
      path: "/user",
      label: "Admin Users",
      icon: <LayoutDashboard size={18} />,
      hasDropdown: true,
      children: [
        { path: "/create-account", label: "Create Acoount", icon: <LayoutDashboard size={18} /> },
      ]
    },
    {
      path: "/live-casino",
      label: "Live Casino",
      icon: <Gamepad size={18} />,
      hasDropdown: true,
      children: [
        { path: "/live-casino", label: "Live Casino", icon: <Gamepad size={18} /> },
        ...(isOwnerRole(localStorage.getItem('activeRole')) ? [
          { path: "/casino-tablelock", label: "Casino Config", icon: <Settings size={18} /> },
        ] : []),
      ]
    },

    ...(isOwnerRole(localStorage.getItem('activeRole')) ? [
      {
        path: "/global-settings",
        label: "Global Settings",
        icon: <Settings size={18} />,
        hasDropdown: true,
        children: [
          { path: "/buffer", label: "Buffer", icon: <LayoutDashboard size={18} /> }
        ]
      },
      {
        path: "/settlement",
        label: "Settlement",
        icon: <CreditCard size={18} />,
        hasDropdown: true,
        children: [
          { path: "/match-settlement", label: "Match Settlement", icon: <LayoutDashboard size={18} /> },
          { path: "/fancy-settlement", label: "Fancy Settlement", icon: <LayoutDashboard size={18} /> },
          { path: "/settled-bets", label: "Settled Bets", icon: <LayoutDashboard size={18} /> },
          { path: "/void-bets", label: "Void Bets", icon: <LayoutDashboard size={18} /> }
        ]
      }
    ] : [])


  ];

  useEffect(() => {
    let foundActive = false;
    menuItems.forEach(item => {
      if (item.children) {
        const activeChild = item.children.some(child => child.path === location.pathname);
        if (activeChild) {
          setExpandedItem(item.path);
          foundActive = true;
        }
      }
    });
    // If no active route found, keep Account Management open by default
    if (!foundActive) {
      setExpandedItem("/account-management");
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (evt) => {
      // Only close sidebar on mobile when clicking outside
      if (window.innerWidth < 1024 && isSidebarOpen && sidebarRef.current && !sidebarRef.current.contains(evt.target)) {
        toggleSidebar();
        console.log("Mobile outside click: Sidebar closed");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSidebarOpen, toggleSidebar]);

  // Restoring scroll position
  useLayoutEffect(() => {
    if (sidebarRef.current && sidebarScrollPos.current) {
      sidebarRef.current.scrollTop = sidebarScrollPos.current;
    }
  }, [location.pathname]); // Re-apply on location change (remount)

  const handleScroll = () => {
    if (sidebarRef.current) {
      sidebarScrollPos.current = sidebarRef.current.scrollTop;
    }
  };

  const toggleExpanded = (path) => {
    setExpandedItem(expandedItem === path ? null : path);
  };

  const handleLogout = () => {
    const admins = localStorage.getItem("adminUsers");
    localStorage.clear();
    if (admins) localStorage.setItem("adminUsers", admins);
    navigate("/login");
    toggleSidebar();
  };

  const handleItemClick = (item) => {
    if (item.hasDropdown) {
      toggleExpanded(item.path);
    } else {
      navigate(item.path);
      // Removed auto-collapse behavior as requested
      // if (window.innerWidth < 1024) {
      //   toggleSidebar();
      // }
    }
  };

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ backgroundColor: "rgba(0, 0, 0, 0.33)", height: "100vh" }}
      ref={sidebarRef}
      onScroll={handleScroll}
      className={`w-full max-w-[180px] h-full shadow-lg transition-transform duration-300  ${sidebarState ? "translate-x-0 z-20" : "-translate-x-full"
        } mt-[16px] flex flex-col relative overflow-y-auto hide-scrollbar`}
    >

      <div className="flex-1">
        {menuItems.map(item => (
          <div key={item.label} className="">
            <button
              style={{ backgroundColor: `${expandedItem === item.path ? "rgba(255, 255, 255, 0.15)" : "rgba(255, 255, 255, 0.15)"}` }}
              onClick={() => handleItemClick(item)}
              className={`w-full flex items-center justify-between px-2 py-3  transition-colors ${expandedItem === item.path
                ? " text-white"
                : item.path === "/live-casino"
                  ? "text-red-500 hover:"
                  : "text-white hover:"
                }`}
            >
              <div className="flex items-center">
                <span
                  className={`text-[14px] text-nowrap font-bold ${item.path === "/live-casino"
                    ? "animate-pulse"
                    : "text-white"
                    }`}
                  style={
                    item.path === "/live-casino"
                      ? {
                        animation: "colorBlink 12s infinite",
                        color: "#ff0000"
                      }
                      : {}
                  }
                >
                  {item.label}
                </span>
              </div>
              {item.hasDropdown && (
                <img src={ArrowAccordion} alt="arrow" className={`transition-transform h-4 w-4  object-contain text-white ${expandedItem === item.path ? "rotate-180" : ""}`} />
              )}
            </button>
            {item.hasDropdown && item.children && (
              <div
                className={`overflow-hidden transition-all duration-200 ${expandedItem === item.path ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  }`}
              >
                <div className="bg-[#171b36] ">
                  {item.children.map(child => (
                    <NavLink
                      key={child.path}
                      to={child.path}
                      // onClick={window.innerWidth < 1024 ? toggleSidebar : undefined} // Removed auto-collapse
                      className={({ isActive }) =>
                        `flex items-center px-6 py-2 text-[14px] text-white font-bold transition-colors no-underline sidebar-nav-link ${isActive ? "" : ""
                        }`
                      }
                    >
                      <span>{child.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Toggle Bar - When Sidebar is Open */}
      {isHovered && sidebarState && (
        <div style={{ height: "100%", width: "18px", background: "#9194aa" }} className=" opacity-50 absolute h-full top-1/2 right-0 transform translate-x-full -translate-y-1/2 bg-white shadow-lg p-2 z-30">
          <button
            onClick={toggleFunction}
            style={{ textDecoration: 'none', marginLeft: "-8px" }}
            className="flex items-center justify-center w-[15px] h-full text-black transition-colors  font-bold"
          >
            <div style={{ marginBottom: "158px" }} className='flex  items-center justify-center '>
              <ChevronLeft style={{ fontWeight: "bold" }} size={16} />
              <ChevronLeft style={{ marginLeft: "-12px", fontWeight: "bold" }} size={16} />

            </div>
          </button>
        </div>
      )}



    </div>
  );
};

export default Sidebar;